import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceArea,
  Tooltip, ComposedChart, BarChart, Bar, Cell, LabelList, Legend,
} from "recharts";

const TIP = { borderRadius: 12, border: "1px solid #E3EFF9", fontSize: 12 };
const AX = { fontSize: 11, fill: "#627D98" };

function Empty() {
  return <div className="h-[220px] flex items-center justify-center text-[#627D98] text-sm">Sem dados no período.</div>;
}

function band(target) {
  return target ? <ReferenceArea y1={target.lower} y2={target.upper} fill="#E3F2FD" fillOpacity={0.7} stroke="#90CAF9" strokeOpacity={0.6} /> : null;
}

export function PeriodChart({ points, target, unit, days }) {
  if (!points.length) return <Empty />;
  const fmt = (ts) => new Date(ts).toLocaleString("pt-PT", days === 1 ? { hour: "2-digit", minute: "2-digit" } : { day: "2-digit", month: "2-digit" });
  let ticks;
  let domain;
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(); end.setHours(23, 59, 59, 0);
  if (days === 1) {
    ticks = Array.from({ length: 9 }, (_, i) => start.getTime() + i * 3 * 3600000).slice(0, 8);
    domain = [start.getTime(), end.getTime()];
  } else {
    start.setDate(start.getDate() - (days - 1));
    const step = days > 14 ? 5 : days > 7 ? 2 : 1;
    ticks = Array.from({ length: Math.ceil(days / step) }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i * step); return d.getTime(); });
    domain = [start.getTime(), end.getTime()];
  }
  return (
    <div data-testid="chart-period">
      <ResponsiveContainer width="100%" height={260}>
        <LineChart key={days} data={points} margin={{ top: 10, right: 16, bottom: 4, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF3F9" vertical={false} />
          {band(target)}
          <XAxis dataKey="t" type="number" scale="time" domain={domain} ticks={ticks} tickFormatter={fmt} tick={AX} tickLine={false} axisLine={{ stroke: "#E3EFF9" }} minTickGap={24} />
          <YAxis tick={AX} tickLine={false} axisLine={false} width={44} domain={["auto", "auto"]} />
          <Tooltip contentStyle={TIP} formatter={(v) => [`${v} ${unit}`, "Glicemia"]}
            labelFormatter={(ts) => new Date(ts).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} />
          <Line type="linear" dataKey="v" stroke="#0D47A1" strokeWidth={2} dot={{ r: 3, fill: "#1E88E5", stroke: "#fff", strokeWidth: 1.5 }} activeDot={{ r: 5 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export function HourlyChart({ hours, target, unit }) {
  if (!hours.some((h) => h.avg != null)) return <Empty />;
  return (
    <div data-testid="chart-hourly">
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={hours} margin={{ top: 10, right: 12, bottom: 4, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF3F9" vertical={false} />
          {band(target)}
          <XAxis dataKey="h" tick={AX} tickLine={false} axisLine={{ stroke: "#E3EFF9" }} tickFormatter={(h) => `${h}h`} interval={2} />
          <YAxis tick={AX} tickLine={false} axisLine={false} width={44} domain={["auto", "auto"]} />
          <Tooltip contentStyle={TIP} labelFormatter={(h) => `${String(h).padStart(2, "0")}:00–${String(h).padStart(2, "0")}:59`}
            formatter={(v, name) => [Array.isArray(v) ? `${v[0]}–${v[1]} ${unit}` : `${v} ${unit}`, name]} />
          <Bar dataKey="range" name="Mín–Máx" fill="#90CAF9" fillOpacity={0.6} radius={4} maxBarSize={10} />
          <Line type="linear" dataKey="avg" name="Média" stroke="#0D47A1" strokeWidth={2.5} dot={{ r: 3, fill: "#1E88E5" }} connectNulls />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function BandsChart({ bands }) {
  if (!bands.some((b) => b.count)) return <Empty />;
  return (
    <div data-testid="chart-bands">
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={bands} margin={{ top: 22, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF3F9" vertical={false} />
          <XAxis dataKey="label" tick={{ ...AX, fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#E3EFF9" }} interval={0} />
          <YAxis tick={AX} tickLine={false} axisLine={false} unit="%" width={48} />
          <Tooltip contentStyle={TIP} formatter={(v, n, p) => [`${v}% · ${p.payload.count} ${p.payload.count === 1 ? "leitura" : "leituras"}`, p.payload.range]} />
          <Bar dataKey="pct" radius={[8, 8, 0, 0]}>
            {bands.map((b) => <Cell key={b.id} fill={b.color} />)}
            <LabelList dataKey="pct" position="top" formatter={(v) => `${v}%`} style={{ fontSize: 11, fill: "#3B526B", fontWeight: 600 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function CarbsInsulinChart({ daily }) {
  if (!daily.some((d) => d.carbs || d.insulin)) return <Empty />;
  return (
    <div data-testid="chart-carbs-insulin">
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={daily} margin={{ top: 10, right: 0, bottom: 4, left: -8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#EDF3F9" vertical={false} />
          <XAxis dataKey="label" tick={AX} tickLine={false} axisLine={{ stroke: "#E3EFF9" }} minTickGap={8} />
          <YAxis yAxisId="c" tick={AX} tickLine={false} axisLine={false} width={44} unit="g" />
          <YAxis yAxisId="i" orientation="right" tick={AX} tickLine={false} axisLine={false} width={36} unit="U" />
          <Tooltip contentStyle={TIP} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="c" dataKey="carbs" name="Hidratos (g)" fill="#F9AB00" radius={[6, 6, 0, 0]} maxBarSize={22} />
          <Bar yAxisId="i" dataKey="insulin" name="Insulina (U)" fill="#1A73E8" radius={[6, 6, 0, 0]} maxBarSize={22} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
